import type { DeckCard } from "@/types/cards";
import type { GameState } from "@/types/game";
import { Rng } from "@/utils/rng";

// 用一期 156 张 deck 初始化牌堆（洗牌）
export function createDrawPile(
  deck: readonly DeckCard[],
  rng: Rng
): DeckCard[] {
  return rng.shuffle(deck);
}

// 摸 n 张牌；返回 { drawn, rest }。摸不够时返回剩余全部。
export function draw(
  pile: readonly DeckCard[],
  n: number
): { drawn: DeckCard[]; rest: DeckCard[] } {
  const drawn = pile.slice(0, n);
  const rest = pile.slice(n);
  return { drawn, rest };
}

// 弃牌：从手牌中移除指定 uid，加入弃牌堆
export function discardCards(
  hand: DeckCard[],
  discardPile: DeckCard[],
  uids: number[]
): { hand: DeckCard[]; discardPile: DeckCard[] } {
  const toDiscard = hand.filter((c) => uids.includes(c.uid));
  const remaining = hand.filter((c) => !uids.includes(c.uid));
  return { hand: remaining, discardPile: [...discardPile, ...toDiscard] };
}

// 重新洗牌：当摸牌堆为空，将弃牌堆洗成新的摸牌堆
export function reshuffle(
  drawPile: DeckCard[],
  discardPile: DeckCard[],
  rng: Rng
): { drawPile: DeckCard[]; discardPile: DeckCard[] } {
  if (drawPile.length > 0) {
    return { drawPile, discardPile };
  }
  return {
    drawPile: rng.shuffle(discardPile),
    discardPile: [],
  };
}

// 拾遗可取的牌：仅判定牌与被动弃牌
// （自己打出的牌、回合结束弃牌不可取）
export function canPickFromDiscard(card: DeckCard): boolean {
  return card.discardReason === "judgment" || card.discardReason === "passive";
}

// 玩家摸 n 张：返回更新后的玩家和牌堆
export function playerDraws(
  state: GameState,
  playerId: string,
  n: number
): { state: GameState; drawn: DeckCard[] } {
  const player = state.players.find((p) => p.id === playerId);
  if (!player) throw new Error(`玩家不存在: ${playerId}`);
  const { drawn, rest } = draw(state.drawPile, n);
  const newPlayer = { ...player, hand: [...player.hand, ...drawn] };
  return {
    state: {
      ...state,
      players: state.players.map((p) => (p.id === playerId ? newPlayer : p)),
      drawPile: rest,
    },
    drawn,
  };
}
